import React, { useEffect, useCallback, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5geodata_worldLow from "@amcharts/amcharts5-geodata/worldLow";
import { Row, Col } from 'react-bootstrap';
import { motion } from "framer-motion";
import "./index.scss";

import NoDataFound from "../../../../../../components/ui/NoDataFound";
import SkeletonLoader from "../../../../../../components/ui/SkeletonLoader";

import { useScanInfoContext } from "../../../../../../pages/ScanInfo/ScanInfoContext";

import { useSelector, useDispatch } from 'react-redux';
import { getVendorRiskCountryStats } from "../../../../../../store/actions/thirdPartyRiskAction";
import { formatNumber, getMapCountryImageUrl } from "../../../../../../utils/util";


const AssetMapChart = () => {

    const countryStatsResponse = useSelector((state) => state?.thirdPartyRisk?.countryStats)

    const dispatch = useDispatch();

    const {
        domainId,
        scanId,
        organizationId,
        triggerStatsFetch,
        setTriggerStatsFetch,
    } = useScanInfoContext();

    const [countryListLoading, setCountryListLoading] = useState(false)

    const fetchCountryStats = useCallback(()=>{
        setCountryListLoading(true)
        const orgParams = organizationId && organizationId.toLowerCase() !== "all" ? `&organization_id=${organizationId}` : ""
        const domainParams = domainId && domainId.toLowerCase() !== "all" ? `&domain=${domainId}&scan_id=${scanId}` : ""
        const params = `${orgParams}${domainParams}`
        dispatch(getVendorRiskCountryStats(params))
    },[organizationId, domainId, scanId, dispatch])

    useEffect(() => {
        if (triggerStatsFetch) {
            fetchCountryStats()
            setTriggerStatsFetch(false);
        }
    }, [triggerStatsFetch, setTriggerStatsFetch, fetchCountryStats]);

    useEffect(()=>{
        if(countryStatsResponse !== null) {
            setCountryListLoading(false)
            let data = []
            if(countryStatsResponse?.status === "failed" || countryStatsResponse === "Not Found") {
                data = []
            } else {
                let list = countryStatsResponse?.stats?.country.filter((item) => item?.name !== null)
                if(list?.length > 0) {
                    data = list.map((data)=>{
                        return {
                            id: data?.id,
                            name: data?.name,
                            value: data?.count,
                            image: getMapCountryImageUrl(data?.name),
                            continent: data?.continent
                        }              
                    })
                } else {
                    data = []
                }
            }
    
            // Create root element
            const root = am5.Root.new("map-chartdiv");
    
            // Set themes
            root.setThemes([
                am5themes_Animated.new(root)
            ]);
    
            // Create the map chart
            const chart = root.container.children.push(am5map.MapChart.new(root, {
                panX: "translateX",
                panY: "translateY",
                projection: am5map.geoMercator()
            }));
    
            // Disable "Created with amCharts 5" label
            root._logo.dispose();
    
            // Create main polygon series for countries
            const polygonSeries = chart.series.push(am5map.MapPolygonSeries.new(root, {
                geoJSON: am5geodata_worldLow,
                exclude: ["AQ"]
            }));
    
            polygonSeries.mapPolygons.template.setAll({
                toggleKey: "active",
                interactive: true,
                fill: am5.color("#35B2FF") // Set color to #35B2FF
            });
    
            // Disable tooltips for polygons
            polygonSeries.mapPolygons.template.set("tooltipText", null);
    
            // Alternatively, disable tooltips completely for the series
            polygonSeries.set("tooltip", null);
    
            chart.set("zoomControl", am5map.ZoomControl.new(root, {}));
    
            // Set clicking on "water" to zoom out
            chart.chartContainer.get("background").events.on("click", () => {
                chart.goHome();
            });
    
            chart.appear(1000, 100);
    
            let bubbleSeries = chart.series.push(
                am5map.MapPointSeries.new(root, {
                    valueField: "value",
                    calculateAggregates: true,
                    polygonIdField: "id",
                })
            );
    
            let circleTemplate = am5.Template.new({});
    
            bubbleSeries.bullets.push(function (root, series, dataItem) {
                let container = am5.Container.new(root, {});
    
                let circle = container.children.push(
                    am5.Circle.new(
                        root,
                        {
                            radius: 20,
                            fillOpacity: 0.7,
                            // fill: am5.color("#60F6FF"), // Static background color for the bubble
                            fill: am5.color(0xff0000), // Static background color for the bubble
                            cursorOverStyle: "pointer",
                            tooltipHTML: `
                                <div class="custom-tooltip-container d-flex align-items-center gap-2">
                                    <div class="country-image">
                                        <img src={image} />
                                    </div>
                                    <div class="tooltip-heading">
                                        <h2 class="m-0">{name}: {value}</h2>
                                        <p class="tooltip-secondary-text">{continent}</p>
                                    </div>
                                </div>`,
                            tooltip: am5.Tooltip.new(root, {
                                getFillFromSprite: false,
                                getStrokeFromSprite: false,
                                autoTextColor: false,
                                background: am5.PointedRectangle.new(root, {
                                    fill: am5.color(0xffffff), // Fixed white background
                                    fillOpacity: 1,           // Fully opaque
                                    stroke: am5.color(0xFFFFFF), // Border color
                                    strokeWidth: 1,           // Border width
                                    pointerBaseWidth: 10,      // Reduce pointer base width
                                    pointerLength: 8,         // Reduce pointer length to minimize space
                                    cornerRadius: 10,          // Optional rounded corners
                                    shadowColor: am5.color(0xeaf1ff), // Shadow color
                                    shadowBlur: 40.9,                 // Amount of blur for the shadow
                                    shadowOffsetX: 5,               // Horizontal shadow offset
                                    shadowOffsetY: 5,
                                    height: 108
                                }),
                            }),
                        },
                        circleTemplate
                    )
                );
        
                // Add value label inside the bubble
                let valueLabel = container.children.push(
                    am5.Label.new(root, {
                        text: "{value.formatNumber('#.')}", // Show the value inside the bubble
                        fill: am5.color("#FFFFFF"), // Text color inside the bubble
                        // fill: am5.color("#00308B"), // Text color inside the bubble
                        populateText: true,
                        centerX: am5.p50,
                        centerY: am5.p50,
                        textAlign: "center",
                        opacity: 1,
                        fontWeight: 600,
                    })
                );
        
                // Store references for later animations
                dataItem.set("circle", circle);
                dataItem.set("valueLabel", valueLabel);
        
                return am5.Bullet.new(root, {
                    sprite: container,
                    dynamic: true,
                });
            });
    
            // minValue and maxValue must be set for the animations to work
            bubbleSeries.set("heatRules", [
                {
                    target: circleTemplate,
                    dataField: "value",
                    min: 10,
                    max: 50,
                    minValue: 0,
                    maxValue: 100,
                    key: "radius",
                },
            ]);
    
            // Set static data
            bubbleSeries.data.setAll(data);
    
            // Trigger animations every 5 seconds
            const interval = setInterval(() => {
                bubbleSeries.dataItems.forEach((dataItem) => {
                    const circle = dataItem.get("circle");
                    const valueLabel = dataItem.get("valueLabel");
        
                    if (circle && valueLabel) {
                        // Animate the radius
                        circle.animate({
                            key: "radius",
                            to: Math.random() * 30 + 20, // Random radius between 20 and 50
                            duration: 1000,
                            easing: am5.ease.out(am5.ease.cubic),
                        });
                    }
                });
            }, 2000);
        
            return () => {
                clearInterval(interval);
                root.dispose();
            };          
        }
    },[countryStatsResponse])

    useEffect(() => {
        

    }, []);

    return (
        <>
            <div className="bg-white-with-shadow p-4 h-100">
                <motion.div
                    className="h-100"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Row>
                        <Col lg={12}>
                            <div className="dashboard-box-container">
                                <p className="heading font-bold">Assets by Country</p>
                            </div>
                            <div id="map-chartdiv" style={{ width: "100%", height: "500px" }}></div>
                        </Col>                        
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <div className="vendor-risk-map country-list-container d-flex w-100 h-100 align-items-center">
                                {
                                    countryListLoading ?
                                    <SkeletonLoader type={"line"} count={4} />
                                    :
                                    <div className="list-of-countries p-4 mt-3">
                                        <p className="heading" style={{ color: "#000" }}>
                                            List of Countries
                                        </p>
                                        <div className="country-list-main-container d-flex align-items-center flex-wrap gap-3">
                                            {
                                                countryStatsResponse?.stats?.country.length > 0 &&
                                                countryStatsResponse?.stats?.country.map((data, index) => {
                                                    if(data?.name !== null) {
                                                        return (
                                                            <>
                                                                <div className="country-list d-flex justify-content-between align-items-center mt-2" key={index}>
                                                                    <div className="countries d-flex align-items-center">
                                                                        <div className="country-image">
                                                                            <img src={getMapCountryImageUrl(data.name)} alt="india" className="me-1" />
                                                                        </div>                                                
                                                                        <div className="text-start">
                                                                            <p className="m-0 font-bold">{data?.name}</p>
                                                                            <p className="m-0 secondary-text">{data?.continent}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <p className="font-bold">{formatNumber(data?.count)}</p>
                                                                    </div>
                                                                </div>
                                                            </>                                                            
                                                        )
                                                    }
                                                    return null
                                                })
                                            }                                            
                                        </div>
                                        {
                                            countryStatsResponse?.stats?.country?.length === 0 &&
                                            <NoDataFound />
                                        }
                                        {
                                            (countryStatsResponse?.status === "failed"  || countryStatsResponse === "Not Found") &&
                                            <NoDataFound noDataText={countryStatsResponse?.description}/>
                                        }
                                    </div>
                                }
                            </div>
                        </Col>
                    </Row>
                </motion.div>
            </div>
        </>
    );
};

export default AssetMapChart;